/**
 * pulsebot/router.ts — the deterministic intent router for PulseBot Phase 1A.
 *
 * matchIntent lowercases the input and checks each intent's keywords via
 * substring .includes(). First match wins, so intents are ordered by
 * specificity. On NO match the bot returns a fallback listing what it can do —
 * it NEVER guesses or executes a handler on a non-match.
 *
 * No fuzzy library (no Fuse.js) — plain substring matching, matching the app's
 * established CommandPalette style.
 *
 * Since the Groq layer landed, "NEVER guesses" means something slightly
 * narrower: the ROUTER still never guesses, but unmatched input is now handed to
 * the LLM fallback (see runPulseBot) instead of dead-ending at the capability
 * list. Everything the registry below matches keeps its exact, deterministic
 * behaviour — the model never gets a say in those.
 */

import { myOpenTickets, overdueInvoices, pendingProcurement, helpCapabilities } from './handlers';
import { searchKb, hasKbMatches } from './kbSearch';
import { askLlm } from './llm';
import type { BotContext, BotResult, Intent } from './types';

/**
 * The intent registry. Order = match priority (first match wins). Each intent's
 * keywords run from most specific phrase to broadest single word; since any
 * keyword hit selects the intent, only the cross-intent ordering matters here.
 *
 * `help` is first (the meta intent): it lists every capability from the
 * capabilities registry, so it stays current as executors are added.
 */
export const INTENTS: Intent[] = [
  {
    id: 'help',
    label: 'Help — what I can do',
    keywords: [
      'help', 'what can you do', 'what can you help', 'capabilities',
      'commands', 'what do you do', 'options',
    ],
    handler: helpCapabilities,
  },
  {
    // KB sits right after `help` and BEFORE the broad noun reads: its keywords
    // are explicit "how-to" phrases, so "how to submit a ticket" resolves to a
    // KB article rather than the my-open-tickets reader (which matches bare
    // 'ticket'). Plain "tickets"/"invoices" carry no KB phrase, so they still
    // reach their own readers. NOTE: no 'help'/'help with' here — that's the
    // capabilities intent (kept distinct).
    id: 'kb-answers',
    label: 'How do I… (knowledge base)',
    keywords: ['how do i', 'how to', 'how can i', 'knowledge base', 'kb', 'article about', 'article on'],
    handler: (_ctx, query) => searchKb(query),
  },
  {
    id: 'my-open-tickets',
    label: 'My open tickets',
    keywords: ['open tickets', 'my tickets', 'ticket', 'tickets'],
    handler: myOpenTickets,
  },
  {
    id: 'overdue-invoices',
    label: 'Overdue invoices',
    keywords: ['overdue', 'unpaid', 'receivable', 'invoice', 'invoices'],
    handler: overdueInvoices,
  },
  {
    id: 'pending-procurement',
    label: 'Pending procurement',
    keywords: ['purchase request', 'pending requests', 'procurement', 'purchase'],
    handler: pendingProcurement,
  },
];

/** Find the first intent whose keywords appear in the input, or null. */
export function matchIntent(input: string): Intent | null {
  const q = input.toLowerCase().trim();
  if (!q) return null;
  for (const intent of INTENTS) {
    if (intent.keywords.some(k => q.includes(k))) return intent;
  }
  return null;
}

/** The no-match answer — lists the intent labels so the user knows what to ask. */
export function fallbackResult(): BotResult {
  return {
    text: "I didn't catch that. I can help with:",
    items: INTENTS.map(i => ({ label: i.label })),
  };
}

/**
 * Route an input to its handler and return the answer.
 *
 * Four tiers, cheapest and most predictable first:
 *
 *   1. Keyword intent  — exact, instant, free. Owns everything it recognises.
 *   2. KB search       — client-side retrieval for natural "how do I" phrasing
 *                        that didn't hit the explicit trigger.
 *   3. LLM fallback    — Groq, via the pulsebot Edge Function. Handles arbitrary
 *                        questions, reports and summaries: the things a keyword
 *                        router structurally cannot do.
 *   4. Capability list — the honest "I didn't catch that" answer.
 *
 * Ordering matters for more than cost. The deterministic tiers stay in front so
 * that every phrase the bot advertises keeps behaving identically — adding a
 * model must not make "my open tickets" start varying between runs. The model
 * only ever sees input the rules already declined.
 *
 * Tier 3 is skipped silently when the Edge Function is unconfigured or disabled,
 * falling through to tier 4. That is what keeps this safe to merge before the
 * GROQ_API_KEY is set: with no key, behaviour is exactly what it was.
 */
export async function runPulseBot(input: string, ctx: BotContext): Promise<BotResult> {
  const intent = matchIntent(input);
  if (intent) return intent.handler(ctx, input);

  const kb = await searchKb(input);
  if (hasKbMatches(kb)) return kb;

  const llm = await askLlm(input);
  if (llm.ok) return { text: llm.text, markdown: true };

  return fallbackResult();
}
